import './polyfills';

import '~/ui/theme/fonts/regola-font.css';
import '~/ui/theme/tailwind-no-preflight.css';
import './app/theming/fonts/material-icons.css';
import './app/theming/fonts/inter.css';
import is from '@sindresorhus/is';
import { createConsola } from 'consola/browser';
import i18next from 'i18next';
import noop from 'lodash/noop';
import { enqueueSnackbar } from 'notistack';
import Maybe from 'true-myth/maybe';

import { createAuthorizationProvider } from '~/shared/authorization';
import { createRealClock } from '~/shared/clock/real-clock';
import { appEnvironmentSchema } from '~/shared/environment/environment';
import { createFeatureFlagService } from '~/shared/feature-flag-service';
import { createHttpFetcher } from '~/shared/http-fetcher';
import type { TranslationOverrides } from '~/shared/internationalization';
import type { Logger } from '~/shared/logger/logger';
import { createQueryClient } from '~/shared/query-client/query-client';
import { createRealtimeEventListenerBuilder } from '~/shared/realtime-event-listener';
import type { ErrorReporter } from '~/shared/validating-http-fetcher/validating-http-fetcher';
import { createValidatingHttpFetcher } from '~/shared/validating-http-fetcher/validating-http-fetcher';

import { App } from './app';
import { initializeColorScheme } from './app/color-scheme/color-scheme';
import { initDataDog, setDatadogTenantId } from './data-dog';
import { StartupErrorFallback } from './FatalErrorFallback';
import { createInternationalization } from './internationalization/internationalization';
import { createCustomizationProvider } from './legacy/customization';
import { createConsoleLogger } from './logger/console-logger';
import { render } from './render-app';
import { setFavicon } from './set-favicon';

initializeColorScheme();

const setupErrorReporter = (datadogEnabled: boolean, logger: Logger): ErrorReporter => {
    if (datadogEnabled) {
        return initDataDog();
    }

    logger.info('DataDog logging disabled due to environment settings');

    return {
        reportError: (message, error, context) => logger.error(message, error, context),
    };
};

const serializeUnknownError = (error: unknown): string => {
    try {
        return JSON.stringify(error) ?? String(error);
    } catch (_: unknown) {
        return String(error);
    }
};

const toError = (error: unknown): Error => {
    if (error instanceof Error) {
        return error;
    }

    if (is.string(error)) {
        return new Error(error);
    }

    return new Error(`Unknown startup error: ${serializeUnknownError(error)}`, { cause: error });
};

const setMuiXLicenseKey = (licenseKey: string, logger: Logger): void => {
    void import('@mui/x-license')
        .then(({ LicenseInfo }) => {
            LicenseInfo.setLicenseKey(licenseKey);
        })
        .catch((error: unknown) => {
            logger.error('Failed to set MUI X license key', error);
        });
};

const createStartupErrorReporter = (logger: Logger): ErrorReporter['reportError'] => {
    return (message, error) => logger.error(message, error);
};

const STARTUP_TIMEOUT_IN_MILLIS = 60_000;

// A startup step that never settles would leave an empty page without an error report, so each
// awaited startup step races one shared deadline that ends startup with the error fallback instead.
// The deadline sits well above the slowest legitimate startup (lock wait plus token request).
const createStartupDeadline = (): {
    readonly clear: () => void;
    readonly race: <T>(step: Promise<T>) => Promise<T>;
} => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const deadline = new Promise<never>((_resolve, reject) => {
        timeoutId = setTimeout(() => {
            reject(new Error(`App startup did not finish within ${STARTUP_TIMEOUT_IN_MILLIS} ms`));
        }, STARTUP_TIMEOUT_IN_MILLIS);
    });
    deadline.catch(noop);

    return {
        clear: () => clearTimeout(timeoutId),
        race: (step) => Promise.race([step, deadline]),
    };
};

const STALE_CHUNK_RELOAD_KEY = `stale-chunk-reload:${import.meta.url}`;

// A new deployment removes the chunks a stale document still references, so a lazy import()
// rejects. Reload once to pull the current index.html; the session flag prevents a reload
// loop when the chunk stays unreachable (offline, blocked request, broken deploy). The flag is
// keyed by the running bundle, so the same tab still recovers after a later deployment.
const reloadOnStaleChunk = (): void => {
    globalThis.addEventListener('vite:preloadError', () => {
        try {
            if (globalThis.sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) !== null) {
                return;
            }

            globalThis.sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, 'true');
        } catch (_: unknown) {
            // sessionStorage unavailable (sandboxed frame, storage disabled): reload without the loop guard
        }

        globalThis.location.reload();
    });
};

export const startApp = async (): Promise<void> => {
    reloadOnStaleChunk();

    const { t: translate } = i18next;
    const logger = createConsoleLogger({ createConsoleTarget: createConsola }, { logLevel: 'silent' });
    let reportStartupError = createStartupErrorReporter(logger);
    const startupDeadline = createStartupDeadline();

    try {
        const environment = appEnvironmentSchema.parse(import.meta.env);
        const apiBaseUrl = new URL(environment.apiBaseUrl);
        const apiRealtimeBaseUrl = new URL(environment.apiRealtimeBaseUrl);
        const { enableDataDog, muiXLicenseKey, refetchOnFocus } = environment;

        const errorReporter = setupErrorReporter(enableDataDog, logger);
        let onRateLimited: (() => void) | undefined;
        const httpFetcherDependencies = { errorReporter, onRateLimited: () => onRateLimited?.() };
        reportStartupError = errorReporter.reportError;
        const cobrainerApiFetcher = createHttpFetcher(httpFetcherDependencies, {
            baseUrl: apiBaseUrl,
            requestHeaders: {
                get CorrelationId() {
                    return globalThis.crypto.randomUUID();
                },
                get RequestId() {
                    return globalThis.crypto.randomUUID();
                },
            },
        });

        const validatingCobrainerApiFetcher = createValidatingHttpFetcher({
            errorReporter,
            httpFetcher: cobrainerApiFetcher,
        });

        const realtimeEventListenerBuilder = createRealtimeEventListenerBuilder({
            baseUrl: apiRealtimeBaseUrl,
        });

        const realtimeApiFetcher = createHttpFetcher(httpFetcherDependencies, {
            baseUrl: apiRealtimeBaseUrl,
        });

        const validatingRealtimeApiFetcher = createValidatingHttpFetcher({
            errorReporter,
            httpFetcher: realtimeApiFetcher,
        });

        const customizationProvider = await startupDeadline.race(
            createCustomizationProvider(
                { validatingCobrainerApiFetcher },
                {
                    location: globalThis.location,
                },
            ),
        );

        const {
            id: _customizationId,
            oidcConfiguration,
            tenantId,
            translationOverrides,
        } = customizationProvider.customization.unwrapOrElse(() => {
            throw new Error('No customization found');
        });

        if (enableDataDog) {
            setDatadogTenantId(tenantId);
        }

        await startupDeadline.race(
            createInternationalization(
                { i18nProvider: i18next },
                { translationOverrides: translationOverrides as Maybe<TranslationOverrides> },
            ),
        );

        const authorizationProvider = await startupDeadline.race(
            createAuthorizationProvider(
                {
                    apiBaseUrl,
                    clock: createRealClock(),
                    logger,
                    validatingHttpFetcher: createValidatingHttpFetcher({
                        errorReporter,
                        httpFetcher: createHttpFetcher(httpFetcherDependencies),
                    }),
                },
                { oidcConfiguration },
            ),
        );

        const { assumedTenantId, isDefaultCustomization } = customizationProvider;
        const tenantHeaders = assumedTenantId.mapOr<Record<string, string>>({}, (tenantId) => ({
            'Tenant-Id': tenantId,
        }));

        validatingCobrainerApiFetcher.authorization = authorizationProvider;
        validatingCobrainerApiFetcher.requestHeaders = {
            ...(isDefaultCustomization ? {} : { 'Customization-Id': _customizationId }),
            ...tenantHeaders,
        };
        realtimeEventListenerBuilder.authorization = authorizationProvider;
        realtimeEventListenerBuilder.requestHeaders = tenantHeaders;
        realtimeApiFetcher.authorization = authorizationProvider;
        realtimeApiFetcher.requestHeaders = tenantHeaders;

        const queryClient = createQueryClient(refetchOnFocus, errorReporter, authorizationProvider.onUnauthorized);

        const featureFlagService = createFeatureFlagService({
            validatingRealtimeApiFetcher,
        });

        // Set history state for initialLoad for BackNavigation
        globalThis.history.replaceState({ initialLoad: true }, globalThis.document.title);

        if (is.string(muiXLicenseKey)) {
            setMuiXLicenseKey(muiXLicenseKey, logger);
        }

        setFavicon(customizationProvider.customization);

        onRateLimited = () =>
            enqueueSnackbar(translate('errors.rate-limited'), {
                key: 'rate-limited',
                preventDuplicate: true,
                variant: 'info',
            });

        render(
            <App
                dependencies={{
                    authorizationProvider,
                    clock: createRealClock(),
                    cobrainerApiFetcher,
                    customizationProvider,
                    featureFlagService,
                    logger,
                    realtimeApiFetcher,
                    realtimeEventListenerBuilder,
                    translate,
                    validatingCobrainerApiFetcher,
                    validatingRealtimeApiFetcher,
                }}
                environment={environment}
                queryClient={queryClient}
            />,
        );
    } catch (error: unknown) {
        try {
            reportStartupError('Failed to start app', toError(error));
        } catch (reportingError: unknown) {
            logger.error('Failed to report startup error', reportingError);
        }

        if (!i18next.isInitialized) {
            try {
                await createInternationalization({ i18nProvider: i18next }, { translationOverrides: Maybe.nothing() });
            } catch (internationalizationError: unknown) {
                logger.error('Failed to initialize startup error translations', internationalizationError);
            }
        }

        render(<StartupErrorFallback error={error} />);
    } finally {
        startupDeadline.clear();
    }
};

void startApp();
